import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAddressCard, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import apiServices from '../../../../../services/apiService';
import { primaryColor } from '../../../../../utils/comon';

const getInputStyle = () => `border border-[#E2E8F0] px-3 py-2 rounded-md w-full text-sm text-[#0F172A] bg-[#F8FAFC] placeholder:text-[#94A3B8] placeholder:text-xs transition-all duration-200 focus:outline-none focus:ring-1 hover:border-gray-400 focus:ring-[${primaryColor()}]`;
const labelStyle = "text-xs mb-1 text-[#0F172A] font-semibold block";

const TenancyBasicForm = forwardRef(({ onNext, siteId, tenantUserId, userData, tenancyData, setGlobalLoading }, ref) => {
    const { user } = useSelector(state => state.auth);
    const [tenancyId, setTenancyId] = useState(null);
    const [formData, setFormData] = useState({
        tenancyStartDate: "",
        tenancyEndDate: "",
        monthlyRentAmount: "",
        securityDeposit: "",
        remarks: ""
    });

    useEffect(() => {
        if (tenancyData) {
            setTenancyId(tenancyData.TenancyId);
            setFormData({
                tenancyStartDate: tenancyData.TenancyStartDate ? tenancyData.TenancyStartDate.split('T')[0] : "",
                tenancyEndDate: tenancyData.TenancyEndDate ? tenancyData.TenancyEndDate.split('T')[0] : "",
                monthlyRentAmount: tenancyData.MonthlyRentAmount || "",
                securityDeposit: tenancyData.SecurityDeposit || "",
                remarks: tenancyData.Remarks || ""
            });
        }
    }, [tenancyData]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    useImperativeHandle(ref, () => ({
        submit: async () => {
            if (!formData.tenancyStartDate) {
                toast.warning("Please fill all required fields");
                return false;
            }

            setGlobalLoading(true);
            try {
                const payload = {
                    id: tenancyId || 0,
                    unitId: Number(user?.branchorunitID || 0),
                    siteId: Number(user?.siteId || 0),
                    ownershipRecordId: Number(user?.ownerShipRecordId || 0),
                    tenantUserId: Number(tenantUserId || 0),
                    tenancyStartDate: formData.tenancyStartDate ? new Date(formData.tenancyStartDate).toISOString() : new Date().toISOString(),
                    tenancyEndDate: formData.tenancyEndDate ? new Date(formData.tenancyEndDate).toISOString() : null,
                    monthlyRentAmount: Number(formData.monthlyRentAmount),
                    securityDeposit: Number(formData.securityDeposit),
                    tenancyStatus: "Active",
                    remarks: formData.remarks || "",
                    createdBy: user?.name || "System",
                    modifiedBy: user?.name || "System",
                    isActiveId: 1
                };


                const isUpdate = Boolean(tenancyId && tenancyId > 0);

                const response = await apiServices({
                    method: isUpdate ? "PUT" : "POST",
                    url: isUpdate ? "/CommUnitTenancy/updateTenancyData" : "/CommUnitTenancy/saveTenancy",
                    data: payload
                });


                if (response.success || response.isSuccess || response.IsSuccess) {
                    toast.success("Tenancy details saved successfully");
                    let parsedData = response.data;
                    if (typeof parsedData === 'string') {
                        try { parsedData = JSON.parse(parsedData); } catch (e) { }
                    }
                    const newTenancyId = response.tenancyId || response.SavedDetails?.tenancyId || parsedData?.tenancyId || parsedData?.SavedDetails?.tenancyId;

                    if (newTenancyId) {
                        onNext(newTenancyId);
                    } else {
                        toast.error("Failed to retrieve tenancy ID from response");
                        return false;
                    }
                    return true;
                } else {
                    toast.error(response.message || "Failed to save tenancy details");
                    return false;
                }
            } catch (error) {
                console.error("Save Tenancy Error:", error);
                toast.error("An error occurred while saving tenancy details");
                return false;
            } finally {
                setGlobalLoading(false);
            }
        }
    }));

    return (
        <div className="w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="border border-[#E2E8F0] rounded-xl overflow-hidden mb-6">
                <div className="bg-white flex items-center justify-between p-4 border-b border-[#E2E8F0] cursor-pointer hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faAddressCard} className="text-[#3B42F6]" />
                        <h3 className="font-bold text-[#0F172A] text-sm">Tenancy Details</h3>
                    </div>
                    <FontAwesomeIcon icon={faChevronDown} className="text-slate-400 text-xs" />
                </div>

                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6 bg-white">
                    <div className="flex flex-col">
                        <label className={labelStyle}>Tenancy Start Date <span className="text-red-500">*</span></label>
                        <input
                            id="tenancyStartDate" type="date" value={formData.tenancyStartDate ? formData.tenancyStartDate.split('T')[0] : ''}
                            onChange={handleInputChange}
                            className={getInputStyle()} required
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className={labelStyle}>Tenancy End Date</label>
                        <input
                            id="tenancyEndDate" type="date" value={formData.tenancyEndDate ? formData.tenancyEndDate.split('T')[0] : ''}
                            onChange={handleInputChange}
                            className={getInputStyle()}
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className={labelStyle}>Monthly Rent Amount</label>
                        <input
                            id="monthlyRentAmount" type="number" value={formData.monthlyRentAmount} onChange={handleInputChange}
                            placeholder="Enter Monthly Rent" className={getInputStyle()}
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className={labelStyle}>Security Deposit</label>
                        <input
                            id="securityDeposit" type="number" value={formData.securityDeposit} onChange={handleInputChange}
                            placeholder="Enter Security Deposit" className={getInputStyle()}
                        />
                    </div>

                    <div className="flex flex-col md:col-span-2">
                        <label className={labelStyle}>Remarks</label>
                        <textarea
                            id="remarks" value={formData.remarks} onChange={handleInputChange}
                            placeholder="Any additional remarks..." className={`${getInputStyle()} resize-none`} rows="3"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
});

export default TenancyBasicForm;
